export interface ScimReference {
    value: string;
    $ref: string;
    display: string;
    $type: string;
}

// Generic reference type for broader reference object structures
export interface Reference {
    DisplayName?: string;
    Id?: string;
    EntityType?: string;
    $ref?: string;
    value?: string;
    display?: string;
    $type?: string;
    [key: string]: any; // Allow additional properties
}

// Type guard to check if an object is a ScimReference
export function isScimReference(obj: any): obj is ScimReference {
    return (
        obj &&
        typeof obj.value === 'string' &&
        typeof obj.$ref === 'string' &&
        typeof obj.display === 'string' &&
        typeof obj.$type === 'string'
    );
}

// Type guard to check if an object is a generic Reference
export function isReference(obj: any): obj is Reference {
    return (
        obj &&
        typeof obj === 'object' &&
        (
            obj.DisplayName !== undefined ||
            obj.Id !== undefined ||
            obj.EntityType !== undefined ||
            obj.$ref !== undefined ||
            obj.value !== undefined ||
            obj.display !== undefined ||
            obj.$type !== undefined
        )
    );
}

// Helper function to safely extract display text from a reference
export function getReferenceDisplay(ref: ScimReference | Reference | string | null | undefined): string {
    if (!ref) return '';
    if (typeof ref === 'string') return ref;
    if (isScimReference(ref)) return ref.display;
    if (isReference(ref)) {
        // Try common display name properties
        if (ref.DisplayName) return ref.DisplayName;
        if (ref.display) return ref.display;
        if (ref.value) return ref.value;
        if (ref.Id) return ref.Id;
    }

    // Handle different reference object structures
    if (typeof ref === 'object' && ref !== null) {
        const obj = ref as any;
        // Try common display name properties
        if (obj.DisplayName) return obj.DisplayName;
        if (obj.displayName) return obj.displayName;
        if (obj.Name) return obj.Name;
        if (obj.name) return obj.name;
        if (obj.display) return obj.display;

        // Try common ID properties as fallback
        if (obj.Id) return obj.Id;
        if (obj.id) return obj.id;
        if (obj.value) return obj.value;
    }

    return String(ref);
}

// Helper function to safely extract the value from a reference
export function getReferenceValue(ref: ScimReference | Reference | string | null | undefined): string {
    if (!ref) return '';
    if (typeof ref === 'string') return ref;
    if (isScimReference(ref)) return ref.value;
    if (isReference(ref)) {
        // Try common ID properties
        if (ref.Id) return ref.Id;
        if (ref.value) return ref.value;
        if (ref.DisplayName) return ref.DisplayName;
        if (ref.display) return ref.display;
    }

    // Handle different reference object structures
    if (typeof ref === 'object' && ref !== null) {
        const obj = ref as any;
        // Try common ID properties
        if (obj.Id) return obj.Id;
        if (obj.id) return obj.id;
        if (obj.value) return obj.value;

        // Try display name properties as fallback
        if (obj.DisplayName) return obj.DisplayName;
        if (obj.displayName) return obj.displayName;
        if (obj.Name) return obj.Name;
        if (obj.name) return obj.name;
    }

    return String(ref);
}

// Helper function to safely extract the type from a reference
export function getReferenceType(ref: ScimReference | Reference | string | null | undefined): string {
    if (!ref) return '';
    if (typeof ref === 'string') return '';
    if (isScimReference(ref)) return ref.$type;
    if (isReference(ref)) {
        // Try common type properties
        if (ref.EntityType) return ref.EntityType;
        if (ref.$type) return ref.$type;
    }

    // Handle different reference object structures
    if (typeof ref === 'object' && ref !== null) {
        const obj = ref as any;
        // Try common type properties
        if (obj.Type) return obj.Type;
        if (obj.type) return obj.type;
        if (obj.$type) return obj.$type;
        if (obj.EntityType) return obj.EntityType;
        if (obj.entityType) return obj.entityType;
    }

    return '';
}
